import { useId, useState } from 'react'
import type { PinVault } from '@application/ports.ts'
import { isLocked, remainingLockMs } from '@domain/rules/pinLockout.ts'
import { es } from '../strings/es.ts'
import { PinField } from './PinField.tsx'

/**
 * Setting and checking the parent PIN. The lockout comes from the domain rule,
 * not from this component: the UI only reports what the rule already decided.
 */
export function PinPanel({
  vault,
  isSet,
  onChanged,
}: {
  vault: PinVault
  isSet: boolean
  onChanged: () => void
}) {
  const [mode, setMode] = useState<'idle' | 'create' | 'enter'>('idle')
  const [first, setFirst] = useState('')
  const [second, setSecond] = useState('')
  const [message, setMessage] = useState<string | null>(null)
  const [tone, setTone] = useState<'ok' | 'error'>('ok')
  const [busy, setBusy] = useState(false)
  const firstId = useId()
  const secondId = useId()

  const reset = (): void => {
    setFirst('')
    setSecond('')
    setMode('idle')
  }

  const say = (text: string, kind: 'ok' | 'error'): void => {
    setMessage(text)
    setTone(kind)
  }

  const save = async (): Promise<void> => {
    if (!/^\d{4,8}$/.test(first)) {
      say(/\D/.test(first) ? es.pin.errorDigits : es.pin.errorShort, 'error')
      return
    }
    if (first !== second) {
      say(es.pin.errorMatch, 'error')
      return
    }
    setBusy(true)
    try {
      await vault.set(first)
      say(es.pin.saved, 'ok')
      reset()
      onChanged()
    } finally {
      setBusy(false)
    }
  }

  const check = async (): Promise<void> => {
    const state = await vault.state()
    const now = new Date()
    if (state.lastFailureAt && isLocked(state.consecutiveFailures, state.lastFailureAt, now)) {
      const seconds = Math.ceil(
        remainingLockMs(state.consecutiveFailures, state.lastFailureAt, now) / 1000,
      )
      say(es.pin.lockedFor(seconds), 'error')
      return
    }
    setBusy(true)
    try {
      const ok = await vault.verify(first)
      if (ok) {
        say(es.pin.opened, 'ok')
        reset()
      } else {
        say(es.pin.errorWrong, 'error')
        setFirst('')
      }
      onChanged()
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="mt-3">
      {mode === 'idle' ? (
        <button
          type="button"
          className="bg-teal rounded px-5 text-base font-bold text-white"
          onClick={() => {
            setMessage(null)
            setMode(isSet ? 'enter' : 'create')
          }}
        >
          {isSet ? es.pin.open : es.pin.create}
        </button>
      ) : (
        <div className="border-rule bg-ground max-w-sm rounded border p-4">
          <p className="text-muted m-0 mb-3 text-sm">{es.pin.explain}</p>
          <PinField
            id={firstId}
            label={mode === 'create' ? es.pin.createField : es.pin.enter}
            value={first}
            onChange={setFirst}
          />
          {mode === 'create' ? (
            <PinField id={secondId} label={es.pin.repeat} value={second} onChange={setSecond} />
          ) : null}
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={busy}
              className="bg-teal rounded px-5 font-bold text-white disabled:opacity-60"
              onClick={() => {
                void (mode === 'create' ? save() : check())
              }}
            >
              {mode === 'create' ? es.pin.save : es.pin.submit}
            </button>
            <button
              type="button"
              className="border-rule rounded border px-5 font-bold"
              onClick={() => {
                setMessage(null)
                reset()
              }}
            >
              {es.pin.cancel}
            </button>
          </div>
        </div>
      )}
      {message ? (
        <p
          role="status"
          className={`mt-2 mb-0 text-sm font-bold ${tone === 'ok' ? 'text-mint' : 'text-coral'}`}
        >
          {message}
        </p>
      ) : null}
    </div>
  )
}
